import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { useStytch } from "@src/packages/api/useStytch";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";

const API_BASE_URL = process.env.EXPO_PUBLIC_API_BASE_URL!;

// The generated client is typed from the unversioned default OpenAPI
// document; this hook pins x-api-version 2026-08-27, whose responses are
// settings-only. Narrow to that contract so dropped persistence metadata
// (id, userId, timestamps) cannot be read.
const apiVersionHeaders = { "x-api-version": "2026-08-27" } as const;

export type NotificationPreferencesData = Pick<
  ResponseBody<paths["/me/notification-preferences"]["get"]>["data"],
  | "medicationsEnabled"
  | "medicationTimings"
  | "appointmentsEnabled"
  | "appointmentTimings"
  | "cycleUpdates"
  | "testResults"
  | "coverageStatus"
  | "documentsEnabled"
  | "paymentsEnabled"
  | "careTeamMessagesEnabled"
  | "clinicUpdatesEnabled"
  | "careTasksEnabled"
  | "pushToken"
>;

export type UpdateNotificationPreferencesPayload = RequestBody<
  paths["/me/notification-preferences"]["patch"]
>;

/**
 * The dashboard API can add a notification preference before this repository's
 * checked-in generated schema is refreshed. Keep that request dynamic rather
 * than hand-editing generated API files; the normal generated path remains the
 * source of truth for every field it already knows about.
 */
export type RuntimeNotificationPreferencesPatch = Record<
  string,
  boolean | string | string[] | null
>;

export type NotificationPreferencesPatch =
  UpdateNotificationPreferencesPayload | RuntimeNotificationPreferencesPatch;

function needsRuntimeNotificationPatch(payload: NotificationPreferencesPatch) {
  return "documentsEnabled" in payload;
}

async function saveRuntimeNotificationPatch(
  token: string,
  payload: RuntimeNotificationPreferencesPatch,
) {
  const response = await fetch(`${API_BASE_URL}/me/notification-preferences`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...apiVersionHeaders,
    },
    body: JSON.stringify(payload),
  });

  if (response.ok) return;

  let error: unknown;
  try {
    error = await response.json();
  } catch {
    // Keep the patient-facing fallback for non-JSON server failures.
  }
  throwApiError(
    error,
    "We couldn't save your notification settings. Please try again.",
    "saveRuntimeNotificationPatch",
    response.status,
  );
}

export function notificationPreferencesQueryKey(patientId?: string) {
  // Notification settings are patient data. Scope the cache just like the
  // other You preferences so a sign-out/sign-in transition never renders a
  // previous patient's notification choices.
  return ["notification-preferences", patientId ?? "unauthenticated"] as const;
}

export function useNotificationPreferences() {
  const { client, patientId } = useApiClient();
  const queryKey = notificationPreferencesQueryKey(patientId);

  return useQuery<NotificationPreferencesData>({
    queryKey,
    enabled: Boolean(patientId),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/me/notification-preferences",
        { headers: apiVersionHeaders },
      );
      if (error) {
        throwApiError(
          error,
          "We couldn't load your notification settings. Please try again.",
          "useNotificationPreferences",
          response?.status,
        );
      }
      return data.data;
    },
  });
}

export function useUpdateNotificationPreferences() {
  const { client, patientId } = useApiClient();
  const stytch = useStytch();
  const queryClient = useQueryClient();
  const queryKey = notificationPreferencesQueryKey(patientId);

  return useMutation({
    mutationFn: async (
      payload: NotificationPreferencesPatch,
    ): Promise<NotificationPreferencesData | null> => {
      if (needsRuntimeNotificationPatch(payload)) {
        const token = stytch.session.getTokens()?.session_jwt;
        if (!token) {
          throw new Error("You need to be signed in to update notifications.");
        }
        await saveRuntimeNotificationPatch(token, payload);
        // The generated response type is intentionally not recreated locally.
        // Keep the optimistic value until the standard query reconciles it.
        return null;
      }

      const { data, error, response } = await client.PATCH(
        "/me/notification-preferences",
        { body: payload, headers: apiVersionHeaders },
      );
      // openapi-fetch returns an error response instead of rejecting. Turn it
      // into a rejected mutation so optimistic UI rolls back and the settings
      // dialog can tell the patient the save did not happen.
      if (error) {
        throwApiError(
          error,
          "We couldn't save your notification settings. Please try again.",
          "useUpdateNotificationPreferences",
          response?.status,
        );
      }
      return data.data;
    },
    onMutate: async (payload: NotificationPreferencesPatch) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<NotificationPreferencesData>(queryKey);
      queryClient.setQueryData<NotificationPreferencesData>(
        queryKey,
        (current) => {
          if (!current) return current;
          return { ...current, ...payload };
        },
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: (preferences) => {
      if (preferences) {
        queryClient.setQueryData(queryKey, preferences);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
